import React, { useEffect } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError } from '../../components';
import QuantitySelector from './QuantitySelector';

import css from './FieldSizeQuantitySelector.module.css';

const FieldSizeQuantitySelectorComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    input,
    meta,
    onChange,
    showLabelAsDisabled,
    availableSizes = [],
    disabled,
    boxSelectWrapperClassName,
    boxSelectOptionClassName,
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { invalid, touched } = meta;

  // Add validation
  useEffect(() => {
    const totalQuantity = Object.values(input.value || {}).reduce((total, qty) => total + qty, 0);
    const hasValidSelection = totalQuantity > 0;

    // Update meta validation state
    if (meta && meta.touched) {
      if (!hasValidSelection) {
        input.onBlur(); // Trigger validation
      }
    }
  }, [input.value, meta, input]);

  const handleQuantityChange = (size, newQuantity) => {
    const currentValue = input.value || {};
    const maxQuantity = availableSizes.find(s => s.key === size)?.currentStock;

    if (newQuantity <= 0) {
      // Remove size if quantity is 0 or negative
      const newValue = { ...currentValue };
      delete newValue[size];
      input.onChange(newValue);
      if (onChange) {
        onChange(newValue);
      }
    } else if (newQuantity <= maxQuantity || typeof maxQuantity !== 'number') {
      const newValue = { ...currentValue, [size]: newQuantity };
      input.onChange(newValue);
      if (onChange) {
        onChange(newValue);
      }
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {label ? (
        <label
          htmlFor={id}
          className={classNames(css.label, { [css.labelDisabled]: showLabelAsDisabled })}
        >
          {label}
        </label>
      ) : null}

      <div className={classNames(css.sizeQuantityWrapper, boxSelectWrapperClassName)}>
        {availableSizes.map(size => {
          const isSelected = !!(input.value || {})[size.key];
          const quantity = (input.value || {})[size.key] || 0;

          return (
            <div
              key={size.key}
              className={classNames(css.sizeQuantityItem, boxSelectOptionClassName, {
                [css.selected]: isSelected,
                [css.itemError]: touched && invalid,
              })}
            >
              <div className={css.sizeSection}>
                <button
                  type="button"
                  className={classNames(css.sizeToggleButton, {
                    [css.sizeSelected]: isSelected,
                    [css.disabled]: disabled || size.currentStock === 0,
                  })}
                  disabled={disabled || size.currentStock === 0}
                  aria-label={`Select size ${size.label}`}
                >
                  {size.label}
                  {size.currentStock !== undefined && (
                    <span className={css.stockInfo}>({size.currentStock})</span>
                  )}
                </button>
              </div>

              <QuantitySelector
                quantity={quantity}
                onQuantityChange={newQuantity => handleQuantityChange(size.key, newQuantity)}
                maxQuantity={size.currentStock}
                disabled={disabled || size.currentStock === 0}
                size={size.key}
              />
            </div>
          );
        })}
      </div>

      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form Field for selecting multiple sizes and quantities
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id Label is optional, but if it is given, an id is also required
 * @param {ReactNode} props.label
 * @param {Array<Object>} props.availableSizes Array of available sizes with key, label, and quantity properties
 * @param {number} props.maxQuantityPerSize Maximum quantity allowed per size (default: 10)
 * @param {boolean} props.disabled Whether the selector is disabled
 * @param {boolean} props.showLabelAsDisabled Whether the label is disabled
 * @param {string?} props.boxSelectWrapperClassName add more style rules to the wrapper div
 * @param {string?} props.boxSelectOptionClassName add more style rules to the option div
 * @returns {JSX.Element} Final Form Field containing size and quantity selector
 */
const FieldSizeQuantitySelector = props => {
  return <Field component={FieldSizeQuantitySelectorComponent} {...props} />;
};

export default FieldSizeQuantitySelector;
